import { useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Input, Label, FormGroup, Button, Badge, Alert, Table, Spinner } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useBlocklist, useAllowlist, useAddEntry, useRemoveEntry, useSyncNow } from './useCrowdsecLists';

/**
 * #161: Verwaltung der CrowdSec-Block-/Whitelisten. Die Blockliste wird als
 * Ban-Decisions in die LAPI gespielt (periodisch erneuert), die Whitelist
 * verhindert Bans für gelistete IPs. Auch die WordPress-Spammer-Bans laufen
 * über dieselben Decisions (origin `valitype-wp-spam`).
 */
function scopeBadge(s) {
  return <Badge color={s === 'Range' ? 'info' : 'secondary'} pill>{s}</Badge>;
}

function ListCard({ title, kind, q, canWrite, withExpiry }) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState('');
  const add = useAddEntry(kind);
  const remove = useRemoveEntry(kind);
  const items = q.data?.items ?? [];

  const onAdd = async () => {
    if (!value.trim()) return;
    const body = { value: value.trim(), reason: reason.trim() || undefined };
    if (withExpiry && expires) body.expires_at = new Date(expires).toISOString();
    await add.mutateAsync(body);
    setValue(''); setReason(''); setExpires('');
  };

  return (
    <Card className="mb-3"><CardBody>
      <CardTitle tag="h6">{title} <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
      {canWrite && (
        <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
          <FormGroup className="mb-0">
            <Label className="small text-muted mb-1">IP oder CIDR</Label>
            <Input bsSize="sm" value={value} onChange={(e) => setValue(e.target.value)} placeholder="203.0.113.7 oder 203.0.113.0/24" style={{ minWidth: 220 }} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="small text-muted mb-1">Grund (optional)</Label>
            <Input bsSize="sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z.B. Bruteforce" style={{ minWidth: 160 }} />
          </FormGroup>
          {withExpiry && (
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Ablauf (optional)</Label>
              <Input type="datetime-local" bsSize="sm" value={expires} onChange={(e) => setExpires(e.target.value)} />
            </FormGroup>
          )}
          <Button size="sm" color="primary" disabled={add.isPending || !value.trim()} onClick={onAdd}>
            {add.isPending ? <Spinner size="sm" /> : 'Hinzufügen'}
          </Button>
        </div>
      )}
      {q.isLoading ? <Spinner size="sm" /> : !items.length ? <div className="small text-muted">Keine Einträge.</div> : (
        <Table size="sm" responsive hover className="small align-middle">
          <thead><tr>
            <th>Wert</th><th>Scope</th><th>Grund</th>
            {withExpiry && <th>Ablauf</th>}
            <th>Zuletzt gesynct</th>{canWrite && <th></th>}
          </tr></thead>
          <tbody>{items.map((it) => (
            <tr key={it.id}>
              <td><code>{it.value}</code>{it.enabled === false && <Badge color="secondary" pill className="ms-1">aus</Badge>}</td>
              <td>{scopeBadge(it.scope)}</td>
              <td className="text-muted">{it.reason || '—'}</td>
              {withExpiry && <td className="text-muted">{it.expires_at ? new Date(it.expires_at).toLocaleString() : '∞'}</td>}
              <td className="text-muted">{it.last_synced_at ? new Date(it.last_synced_at).toLocaleString() : '—'}</td>
              {canWrite && <td><Button size="sm" color="link" className="p-0 text-danger" disabled={remove.isPending} onClick={() => remove.mutateAsync({ id: it.id })}>entfernen</Button></td>}
            </tr>
          ))}</tbody>
        </Table>
      )}
    </CardBody></Card>
  );
}

export function NexusCrowdsecPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const block = useBlocklist();
  const allow = useAllowlist();
  const sync = useSyncNow();
  const decisionsEnabled = block.data?.decisions_enabled ?? allow.data?.decisions_enabled;

  return (
    <div>
      <PageHeader title="CrowdSec · Block-/Whitelisten"
        subtitle="Kuratierte IP-Listen, die regelmäßig als CrowdSec-Decisions eingespielt werden." />
      {decisionsEnabled === false && (
        <Alert color="warning" className="small">
          CrowdSec-Decisions sind derzeit nicht konfiguriert (Machine-Auth fehlt).
          Einträge werden gespeichert, aber erst nach Konfiguration eingespielt.
        </Alert>
      )}
      <Alert color="info" className="small">
        Die <strong>Blockliste</strong> wird als Ban-Decisions in die LAPI gespielt und vor Ablauf
        automatisch erneuert. Die <strong>Whitelist</strong> verhindert Bans für gelistete IPs
        (auch WordPress-Spammer-Bans respektieren sie). Der Sync läuft periodisch; „Jetzt syncen"
        stößt ihn sofort an.
      </Alert>
      {canWrite && (
        <div className="mb-3">
          <Button size="sm" color="secondary" disabled={sync.isPending} onClick={() => sync.mutateAsync({})}>
            {sync.isPending ? <Spinner size="sm" /> : 'Jetzt syncen'}
          </Button>
        </div>
      )}
      <Row>
        <Col lg={6}><ListCard title="Blockliste" kind="blocklist" q={block} canWrite={canWrite} withExpiry /></Col>
        <Col lg={6}><ListCard title="Whitelist" kind="allowlist" q={allow} canWrite={canWrite} /></Col>
      </Row>
    </div>
  );
}
